import {
	useState,
	useEffect,
	useMemo, memo,
} from 'react'
import prop from 'ramda/src/prop'
import path from 'ramda/src/path'
import pipe from 'ramda/src/pipe'
import equals from 'ramda/src/equals'
import moment from 'moment'
import dropEventConnector from 'root/src/client/logic/dropEvent/connectors/dropEventConnector'
import backArrow from 'root/src/client/public/icons/ic-back-arrow.svg'
import { ButtonNew } from 'root/src/client/v2/common/components/form/ButtonNew'
import FAQSection from 'root/src/client/web/static/FAQ/FAQSection'
import MaxWidthContainer from 'root/src/client/web/base/MaxWidthContainer'
import { discordUrl } from 'root/src/shared/constants/mail'
import {
	dropDareDifficultyLevelValue,
	dropEventSubscriptionStatusEnum,
} from 'root/src/shared/constants/dropEvent'
import isPastToday from 'root/src/shared/util/isPastToday'
import DropWidget from 'root/src/client/web/base/DropWidget'
import { SPONSORSHIP_DETAILS_MODULE_ID } from 'root/src/shared/descriptions/modules/moduleIds'
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest'
import dropEndsIn from 'root/src/client/public/dropEndsIn.svg'
import dropComingSoon from 'root/src/client/public/dropComingSoon.svg'
import { faqAccordionIds } from 'root/src/shared/constants/accordionIds'
import { useMaxDaresByPlatform } from 'root/src/client/logic/dropEvent/hooks/useMaxDaresByPlatform'
import { RaceForRewards } from 'root/src/client/v2/drop/features/RaceForRewards'
import { useRouter } from 'next/router'
import { routeUrls } from 'root/src/shared/descriptions/routes/routeUrls'
import { dropEndpointIds } from 'root/src/shared/drops/drops.constants'
import { TourProvider, useTour } from '@reactour/tour'
import { DROP_ONBOARDING_INITIAL_STEPS } from 'root/src/client/web/record/DropEvent/onboarding'
import { useDocumentOverflow } from 'root/src/client/v2/common/contexts/modal.context'
import { useOnboardingQueryParam } from 'root/src/client/v2/onboarding/hooks/useOnboardingQueryParam'
import { CreatorOnboardingStep, OnboardingStateType } from 'root/src/shared/onboarding/onboarding.types'
import { trpc } from 'root/src/client/v2/trpc/trpc'
import LiveCountdown from './LiveCountdown'
import HeroSection from './HeroSection'
import DaresSection from './DaresSection'
import HowToSection from './HowToSection'
import content from './eventStaticContent'
import { DropEventContext } from './contexts/dropEvent.context'

const isDropActive = pipe(
	prop('status'),
	equals(dropEventSubscriptionStatusEnum.ACTIVE),
)

const faqContent = ({
	gameName,
	minTwitchCCVRequired,
	canAccept,
	maxDares,
	ccv = 0,
	daresCount,
}) => ({
	items: [
		{
			id: faqAccordionIds.WHAT_ARE_DROPS,
			title: 'What are drops?',
			content: 'Drops are a series of dares created by video game publishers where creators can win rewards for fans.',
		},
		{
			id: faqAccordionIds.WHAT_ARE_DARES,
			title: 'What are dares?',
			content: 'Dares are challenges for creators to complete that are rewarded with loot. Game keys, DLC, swag, and more with the biggest prizes going to the most difficult dares!',
		},
		{
			id: faqAccordionIds.WHAT_DO_I_NEED_TO_PARTICIPATE,
			title: 'What do I need to participate?',
			content: `The minimum CCV required for this drop is ${minTwitchCCVRequired}! ${(canAccept && daresCount) ? `Based on your CCV of ${ccv} you can accept ${maxDares} ${maxDares === 1 ? 'Dare' : 'Dares'}` : ''}`,
		},
		{
			id: faqAccordionIds.WHERE_CAN_I_GET_GAME,
			title: `Where can I get ${gameName}?`,
			content: 'Once you accept your first dare you’ll receive an email from us with your key if it’s needed to play. You can also access your keys in the <a href="https://daredrop.com/my-keys" target="_blank">My Keys</a> page on Dare Drop.',
		},
		{
			id: faqAccordionIds.HOW_MANY_DARES_CAN_I_DO,
			title: 'How many dares can I do?',
			content: 'Accept any/all dares that you think you can complete and take on the challenge! Some games limit the number of dares you are eligible for based on the size of your channel.',
		},
		{
			id: faqAccordionIds.HOW_DO_I_VIEW_MY_ACCEPTED_COMPLETED_DARES,
			title: 'How do I view my accepted/completed dares?',
			content: 'You can view your current dares and their status by going to “My Profile” in the drop down at the top right corner of the page.',
		},
		{
			id: faqAccordionIds.HOW_DO_I_COMPLETE_A_DARE,
			title: 'How do I complete a dare?',
			content: 'After completing the dare on your channel, you can upload your clip by clicking “Deliver Dare Video” for the given dare in your Profile in the drop down menu on the top right. \n\nOnce uploaded, our team will review your clip, verify it’s completion, and send you your reward!',
		},
		{
			id: faqAccordionIds.WILL_I_GET_PENALIZED_FOR_FAILING_NOT_COMPLETING_A_DARE,
			title: 'Will I get penalized for failing/not completing a dare?',
			content: 'No. You’re only obligated to stream or post content for the game when you claim a key.',
		},
		{
			id: faqAccordionIds.HOW_DO_I_ACCESS_MY_REWARDS,
			title: 'How do I access my rewards?',
			content: 'Once a completed dare is verified you will receive an email from Dare Drop with your rewards. You can also visit the <a href="https://daredrop.com/my-keys" target="_blank">My Keys</a> page on Dare Drop to view both Initial Keys and your Key Rewards.',
		},
		{
			id: faqAccordionIds.IS_THIS_OFFER_AVAILABLE_IN_ALL_REGIONS,
			title: 'Is this offer available in all regions?',
			content: 'If there are keys listed for a specific region, then those drops are only for that region. If no specific region is listed, it’s available for everyone!',
		},
		{
			id: faqAccordionIds.WHAT_IF_I_HAVE_MORE_QUESTIONS_OR_NEED_HELP,
			title: 'What if I have more questions or need help?',
			content: `Join our discord at <a href="https://${discordUrl}" target="_blank">${discordUrl}</a> or shoot us an email at <a href = "mailto: support@daredrop.com">support@daredrop.com</a>`,
		},
	],
})

const styles = {
	root: {
		display: 'block',
	},
	fullSize: {
		width: '100%',
		height: '100%',
	},
	flex: {
		display: 'flex',
	},
	flexCenter: {
		justifyContent: 'center',
		alignItems: 'center',
	},
}

const DropEvent = ({
	classes, preview, isAdmin, dropEvent, platforms, isAuthenticated,
	isBrand, userId, ccv, redirectAndPrePopulate, regions, setEmailSubscriptionDrop,
	displayModal, userCampaignData, canEarnPoints, dispatch,
	currentRouteParams, tokens, isCreator, displayHowToEarnPointsModal,
	followerCountByPlatforms, parentUserId,
}) => {
	const {
		dares, followersBreakpoints, ccvBreakpoints, totalPrizes,
		id: campaignId, assets, gameDescriptionBody, liveDate, isEmailSubscribed,
		initialProduct, isGameFree, mnemonicId, expirationDate, dropTitle, estimatedPointsToEarn,
		canUserReclaimReservation, pointsLimit, pointsAwarded, pointsReserved, isAbleToParticipate, isCommunityDrop,
		youtubeURL, pointsApplicationState, pointsApplicationsEnabled, pointsStreamLimit, isCreatorPointsAllowlisted,
		discordInvitationLink,
	} = dropEvent || {}

	const [isOnboardingStepCaptured, setIsOnboardingStepCaptured] = useState(false)
	const completeOnboardingStepMutation = trpc.onboarding.completeStep.useMutation()
	const queryUtils = trpc.useUtils()

	const handleCreatorOnboarding = async () => {
		const creatorOnboardingState = await queryUtils.creators.getCreatorOnboardingState.fetch()
		if (creatorOnboardingState?.hasFoundDrop) {
			return
		}
		queryUtils.creators.getCreatorOnboardingState.setData(undefined, () => ({
			...creatorOnboardingState,
			hasFoundDrop: true,
		}))
		await completeOnboardingStepMutation.mutateAsync({
			type: OnboardingStateType.Creator,
			step: CreatorOnboardingStep.HasFoundDrop,
		})
	}

	useEffect(() => {
		if (isCreator && !isOnboardingStepCaptured) {
			setIsOnboardingStepCaptured(true)
			void handleCreatorOnboarding()
		}
	}, [isCreator, isOnboardingStepCaptured])

	const maxDares = useMaxDaresByPlatform({ fallbackDrop: dropEvent })

	const canAccept = !isAdmin && !isBrand && maxDares > 0

	const refetchDropEvent = async () => {
		const routeParamsMnemonicId = currentRouteParams?.recordId
		return dispatch(
			apiRequest(dropEndpointIds.DROP_GET_EVENT, { recordId: routeParamsMnemonicId }),
		)
	}

	const [submitting, setSubmitting] = useState(false)
	const [subscribed, setSubscribed] = useState(isEmailSubscribed)
	const [comingSoon, setComingSoon] = useState(!isPastToday(liveDate) && !preview)
	const [customDate, setCustomDate] = useState()

	const showTermsModal = !userCampaignData?.termsAccepted
		&& canEarnPoints
		&& !isAdmin
		&& !isBrand
		&& pointsLimit
		&& pointsLimit - pointsAwarded > 0
		&& (!pointsApplicationsEnabled || isCreatorPointsAllowlisted)

	useEffect(() => {
		if (showTermsModal) {
			displayHowToEarnPointsModal()
		}
	}, [showTermsModal])

	const toggleEmailSubscription = async (setError) => {
		try {
			await setEmailSubscriptionDrop(campaignId, !subscribed, dropEvent)
			setSubscribed(prev => !prev)
		} catch (err) {
			setError(err.error)
		}
	}
	const gameName = path(['gameObj', 'label'], dropEvent) || path(['campaignProduct'], dropEvent)

	const isDropOwner = [userId, parentUserId].includes(dropEvent.brand.id)

	const minTwitchCCVRequired = useMemo(() => {
		if (ccvBreakpoints.length) {
			const min = ccvBreakpoints.reduce((prev, curr) => (prev.ccv < curr.ccv ? prev : curr))
			return min.ccv + 1
		}
		return 0
	}, [ccvBreakpoints])

	const toggleComingSoon = () => {
		setComingSoon(prevState => !prevState)
		if (isAdmin && isPastToday(liveDate)) setCustomDate(moment().add(20000, 'minutes'))
	}

	const pointsToEarn = comingSoon ? undefined : estimatedPointsToEarn
	const isExpired = isPastToday(expirationDate)

	const displaySponsorshipDetailsModal = () => {
		displayModal(SPONSORSHIP_DETAILS_MODULE_ID, {
			maxDares,
			canEarnPoints,
			acceptedDares: userCampaignData?.creatorAcceptedDares,
			pointsReservationDueDate: userCampaignData?.termsAccepted?.pointsReservationDueDate,
			reservedPointsTotal: userCampaignData?.termsAccepted?.reservedPointsTotal,
			termsAcceptedDate: userCampaignData?.termsAccepted?.created,
			pointsEarned: userCampaignData?.pointsEarned,
		})
	}

	const router = useRouter()

	const dropEventContextValue = useMemo(() => ({
		subscribed,
		toggleEmailSubscription,
		dropEvent,
	}), [subscribed, toggleEmailSubscription, dropEvent])

	const sortedDares = useMemo(() => {
		const enabledDares = dares.filter(dare => !dare.disabled)

		const daresByExpirationDate = enabledDares
			.filter(dare => dare.expirationDate)
			.sort((a, b) => (new Date(a.expirationDate) > new Date(b.expirationDate) ? 1 : -1))

		const daresByDifficulty = enabledDares
			.filter(dare => !dare.expirationDate)
			.sort((a, b) => dropDareDifficultyLevelValue[a.level] - dropDareDifficultyLevelValue[b.level])

		return [...daresByExpirationDate, ...daresByDifficulty]
	}, [dares])

	return (
		<OnboardingProvider>
			<DropEventContext.Provider value={dropEventContextValue}>
				{!comingSoon && !isExpired && (
					<LiveCountdown
						date={expirationDate}
						onFinish={async () => {
							await refetchDropEvent()
						}}
						headerText="Drop ends in"
						bannerImage={dropEndsIn.src}
					/>
				)}
				{comingSoon && (
					<LiveCountdown
						date={customDate || liveDate}
						onFinish={async () => {
							await refetchDropEvent()
							setComingSoon(false)
						}}
						headerText="Drop is coming soon"
						bannerImage={dropComingSoon.src}
					/>
				)}
				<DropWidget {...{ mnemonicId, isAdmin, comingSoon, toggleComingSoon }} />
				<MaxWidthContainer className={classes.root}>
					{preview && (
						<ButtonNew
							className="fixed right-24 top-40 flex gap-3 z-50 items-center justify-center"
							onClick={() => {
								const dropId = new URL(window.location.href).searchParams.get('dropId')
								void router.replace(routeUrls.addCampaign({ dropId }))
							}}
						>
							<img width="18" height="18" src={backArrow.src} alt="Arrow left" /> Back
						</ButtonNew>
					)}
					<HeroSection
						discordInvitationLink={discordInvitationLink}
						assets={assets}
						gameName={dropTitle || gameName}
						brandName={path(['brand', 'brandName'], dropEvent)}
						gameDescriptionBody={gameDescriptionBody}
						regions={regions}
						platforms={platforms}
						dares={sortedDares}
						campaignId={campaignId}
						submitting={submitting}
						setSubmitting={setSubmitting}
						availablePrizes={{
							lootRewards: prop('availablePrizes', dropEvent),
							initialProduct: prop('initialProduct', dropEvent),
						}}
						isDropOver={!isDropActive(dropEvent) && !preview && !isAdmin && !isDropOwner}
						isBrand={isBrand}
						isCreator={isCreator}
						status={prop('status', dropEvent)}
						redirectAndPrePopulate={redirectAndPrePopulate}
						liveDate={liveDate}
						displayModal={displayModal}
						comingSoon={comingSoon}
						isAuthenticated={isAuthenticated}
						emailSubscription={subscribed}
						toggleEmailSubscription={toggleEmailSubscription}
						totalPrizes={totalPrizes}
						initialProduct={initialProduct}
						pointsToEarn={pointsToEarn}
						isGameFree={isGameFree}
						canEarnPoints={canEarnPoints}
						pointsLimit={pointsLimit}
						youtubeURL={youtubeURL}
						pointsAwarded={pointsAwarded}
						isAdmin={isAdmin}
						displaySponsorshipDetailsModal={displaySponsorshipDetailsModal}
						isCreatorAcceptedTerms={userCampaignData?.termsAccepted}
						pointsReserved={pointsReserved}
						showTermsModal={showTermsModal}
						canUserReclaimReservation={canUserReclaimReservation}
						displayHowToEarnPointsModal={displayHowToEarnPointsModal}
						isAbleToParticipate={isAbleToParticipate}
						isCommunityDrop={isCommunityDrop}
						canAccept={canAccept}
						pointsApplicationState={pointsApplicationState}
						pointsApplicationsEnabled={pointsApplicationsEnabled}
						pointsStreamLimit={pointsStreamLimit}
						isCreatorPointsAllowlisted={isCreatorPointsAllowlisted}
					/>
					<RaceForRewards />
					{
						!isAuthenticated && (
							<HowToSection
								pageContent={prop('howToSection', content)}
								displayModal={displayModal}
							/>
						)
					}
				</MaxWidthContainer>

				{dares && (
					<DaresSection
						{...{
							refetchDropEvent,
							dares: sortedDares,
							gameName,
							campaignId,
							submitting,
							setSubmitting,
							content: prop('daresSection', content),
							comingSoon,
							emailSubscription: subscribed,
							toggleEmailSubscription,
							showTermsModal,
							pointsToEarn,
							canEarnPoints,
							tokens,
							dropEvent,
							followerCountByPlatforms,
							averageCcv: ccv,
							followersBreakpoints,
							ccvBreakpoints,
							isAuthenticated,
							isBrand,
							creatorAcceptedDares: userCampaignData?.creatorAcceptedDares,
							isAbleToParticipate,
							canAccept,
							drop: dropEvent,
						}}
					/>
				)}
				<MaxWidthContainer>
					<FAQSection
						pageContent={faqContent({
							gameName,
							minTwitchCCVRequired,
							canAccept,
							ccv,
							daresCount: dares.length,
							maxDares: maxDares > dares.length ? dares.length : maxDares,
						})}
					/>
				</MaxWidthContainer>
			</DropEventContext.Provider>
			<OnboardingTrigger dropEvent={dropEvent} />
		</OnboardingProvider>
	)
}

const OnboardingProvider = memo(({ children }) => {
	const overflow = useDocumentOverflow()
	return (
		<TourProvider
			showCloseButton={false}
			showNavigation={false}
			showPrevNextButtons={false}
			afterOpen={() => {
				overflow.hide()
			}}
			beforeClose={() => {
				overflow.show()
			}}
			styles={{
				badge: () => ({
					display: 'none',
				}),
				maskRect: props => ({
					...props,
					fill: 'none',
					background: 'transparent',
				}),
				maskArea: props => ({
					...props,
					fill: '#060606',
				}),
				maskWrapper: props => ({
					...props,
					fill: 'transparent',
				}),
				highlightedArea: props => ({
					...props,
					fill: 'transparent',
				}),
				clickArea: props => ({
					...props,
					fill: '#060606',
				}),
				popover: props => ({
					...props,
					borderRadius: 12,
				}),
			}}
			steps={[]}
		>
			{children}
		</TourProvider>
	)
})

const OnboardingTrigger = memo(({ dropEvent }) => {
	const [isOnboarding] = useOnboardingQueryParam()
	const [isLoaded, setIsLoaded] = useState(false)

	const tour = useTour()

	useEffect(() => {
		if (isOnboarding && !tour.isOpen && dropEvent && !isLoaded) {
			tour.setSteps(DROP_ONBOARDING_INITIAL_STEPS(dropEvent))
			tour.setCurrentStep(1)
			tour.setIsOpen(true)
			setIsLoaded(true)
		}
	}, [dropEvent])

	return null
})

export default dropEventConnector(DropEvent, styles, 'MuiDareEvent')
